/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  type ComponentProps,
  type ElementType,
} from 'react';
import classnames from 'classnames';
import useKeys from 'effects/keys';
import './Collapsible.scss';

const DEFAULT_ON_OPEN = () => {};
const DEFAULT_ON_CLOSE = () => {};
const DEFAULT_ON_CLOSED = () => {};

interface CollapsibleProps extends Omit<
  ComponentProps<'div'>,
  'onOpen' | 'onClose'
> {
  component?: ElementType;
  active?: boolean;
  open?: boolean;
  onOpen?: (event: { target: HTMLElement | null }) => void;
  onClose?: () => void;
  onClosed?: (event: { target: HTMLElement | null }) => void;
}

/**
 * This is the Collapsible component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Collapsible({
  className,
  component = 'div',
  children,
  active = false,
  open = false,
  onOpen = DEFAULT_ON_OPEN,
  onClose = DEFAULT_ON_CLOSE,
  onClosed = DEFAULT_ON_CLOSED,
  ...props
}: CollapsibleProps) {
  const container = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLElement>(null);
  const TagName = component;
  const [height, setHeight] = useState<number>();
  const [ready, setReady] = useState(false);
  const [opened, setOpened] = useState(false);

  useKeys('ensemble-actors', active, {
    esc: e => {
      e.preventDefault();
      onClose();
    },
  });

  useLayoutEffect(() => {
    function onResize() {
      if (!open) {
        setReady(false);
      }

      setHeight(content.current!.offsetHeight);
    }
    window.addEventListener('resize', onResize);
    const timeout = setTimeout(onResize, 200);
    return function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useLayoutEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (!ready && height) {
      timeout = setTimeout(() => {
        setReady(true);
      }, 160);
    }
    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [height, ready]);

  useLayoutEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const { current } = container;
    if (height) {
      if (open) {
        current!.style.height = `${height}px`;
      } else {
        current!.style.height = `${height}px`;
        timeout = setTimeout(() => {
          current!.style.height = '0';
        }, 160);
      }
    }

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [height, open]);

  useEffect(() => {
    if (opened) {
      onOpen({ target: container.current });
    } else {
      onClosed({ target: container.current });
    }
  }, [opened, onOpen, onClosed]);

  return (
    <div
      ref={container}
      {...props}
      aria-hidden={open ? undefined : 'true'}
      className={classnames('collapsible', className, { init: !ready })}
      onTransitionEnd={({ target }) => {
        if (target === container.current) {
          setOpened(open);
        }
      }}
    >
      <TagName ref={content}>{children}</TagName>
    </div>
  );
}

export default Collapsible;
